import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import debounce from 'just-debounce-it';

import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';

import {
  searchClear,
  searchRequest,
  setSearchQuery,
} from '../../../modules/search/actions';

import IconClose from '../../../../../images/icons/misc/icon-close.svg';
import IconSearch from '../../../../../images/icons/misc/icon-search.svg';

import { getClientString } from '../../../utils';

import styles from './Search.module.scss';

const Search = ({
  onQuery,
  onQueryClear,
  setQuery,
  queryValue,
  loadingResults,
  widthSize,
  onInputFocusChange = () => { },
  fullWidth = false,
}) => {
  const upperContext = React.useContext(ChildrenPropsContext) || {};
  const {
    customization: {
      searchPosition = 'header',
      searchPlaceholder = 'default'
    } = {},
  } = upperContext;

  const isSearchPlaceholderCustom = searchPlaceholder === 'custom';

  const { t } = useImprovedTranslation();
  const { clientSite, clientName } = useReduxBridge() || {};

  const clientString = getClientString(clientName, clientSite);

  const [isFocused, setIsFocused] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const inputRef = useRef();

  const debouncedQuery = useMemo(() => debounce(onQuery, 300), []);

  const isCollapse = widthSize < 1365;

  const onSearchClick = () => {
    if (isCollapse) {
      setShowSearch(!showSearch);
    }

    setTimeout(() => {
      inputRef.current.focus();
    }, 0);
  };

  const clearQuery = () => {
    debouncedQuery.cancel();
    onQueryClear();
  };

  const dismissSearch = () => {
    setIsFocused(false);
    setShowSearch(false);
    clearQuery();
  };

  const onInputChange = (e) => {
    if (e.target.value !== '') {
      setQuery(e.target.value);
    } else {
      clearQuery();
    }
  };

  const onInputFocus = () => {
    setIsFocused(true);
    onInputFocusChange();
  };

  const onInputBlur = () => {
    setIsFocused(false);
  };

  useEffect(() => {
    const searchShortcutListener = event => {
      const isSearchFocused = document.activeElement === inputRef.current;
      const userAlreadyInputing = document.activeElement.tagName === 'INPUT';
      if (event.key === 'F' && !isSearchFocused && !userAlreadyInputing) {
        inputRef.current.focus();
      }

      if (event.key === 'Escape' && isSearchFocused) {
        inputRef.current.blur();
        dismissSearch();
      }
    };

    dismissSearch();
    window.addEventListener('keyup', searchShortcutListener);

    return (
      () => {
        window.removeEventListener('keyup', searchShortcutListener);
      }
    );
  }, []);

  useEffect(() => {
    if (queryValue === '') {
      debouncedQuery.cancel();
    }

    if (queryValue.length > 2) {
      debouncedQuery(queryValue);
    }
  }, [queryValue, debouncedQuery]);

  const searchClass = classNames({
    [styles.search]: true,
    [styles['search--visible']]: isCollapse && showSearch,
    [styles['search--fullWidth']]: fullWidth,
    [styles['search--inHeader']]: searchPosition === 'header',
  });

  const inputClass = classNames({
    [styles.input]: true,
    [styles['input--visible']]: isCollapse && showSearch,
    '.gtm_cta': true,
  });

  return (
    <div className={searchClass}>
      <i
        role="button"
        onClick={onSearchClick}
        data-hook="search-button"
        className=".gtm_cta"
      >
        <IconSearch className={styles.iconSearch} />
      </i>
      <input
        type="text"
        data-hook="search-top"
        className={inputClass}
        name="search-top"
        autoComplete="cc-nope"
        ref={inputRef}
        value={queryValue}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        onChange={onInputChange}
        {...(!isSearchPlaceholderCustom ? { placeholder: t('Search Events and Markets') } : {})}
      />
      {isSearchPlaceholderCustom && !queryValue && !isFocused && (
        <div className={styles.placeholder}>
          <span>{t('Search')}</span>
          {'  '}
          <span className={styles.placeholderBrand}>{clientString}</span>
        </div>
      )}
      {loadingResults && (
        <svg className={styles.loader} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="45" />
        </svg>
      )}
      {Boolean(queryValue) && (
        <i
          role="button"
          className={styles.closeButton}
          onClick={dismissSearch}
        >
          <IconClose className={styles.iconClose} />
        </i>
      )}
    </div>
  );
};

Search.propTypes = {
  onQuery: PropTypes.func.isRequired,
  onQueryClear: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
  queryValue: PropTypes.string.isRequired,
  loadingResults: PropTypes.bool.isRequired,
  widthSize: PropTypes.number.isRequired,
  onInputFocusChange: PropTypes.func,
  fullWidth: PropTypes.bool,
};

const mapDispatchToProps = {
  onQuery: searchRequest,
  onQueryClear: searchClear,
  setQuery: setSearchQuery,
};

function mapStateToProps(state) {
  return {
    widthSize: state.getIn(['application', 'widthSize']),
    loadingResults: state.getIn(['local', 'search', 'loading']),
    queryValue: state.getIn(['local', 'search', 'value']),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Search);
